import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  TextField,
  Grid,
  IconButton,
  Collapse,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from '@mui/material';
import { Download, EventRepeat, ExpandLess, ExpandMore, Phone, WhatsApp, ShoppingCart } from '@mui/icons-material';
import MonthlyRefillOrderReview from './MonthlyRefillOrderReview';
import dayjs from 'dayjs';
import { monthlyRefillsAPI, lookupAPI } from '../services/api';
import {
  Store,
  MonthlyRefillCustomer,
  MonthlyRefillItem,
  MonthlyRefillsMetadata,
  MonthlyRefillItemsMetadata,
} from '../types';

interface Props {
  selectedStore: Store | null;
}

const inr = (n: string | number | null) =>
  n == null || n === ''
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const num = (n: string | number | null, dp = 0) =>
  n == null || n === '' ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: dp });

function fmtDate(val: string | null) {
  if (!val) return '—';
  return dayjs(val).format('DD-MMM-YY');
}

/** 1 -> 1st, 2 -> 2nd, 23 -> 23rd */
function ordinal(d: number) {
  if (d >= 11 && d <= 13) return `${d}th`;
  switch (d % 10) {
    case 1: return `${d}st`;
    case 2: return `${d}nd`;
    case 3: return `${d}rd`;
    default: return `${d}th`;
  }
}

/** "2nd" when the window is a single day, otherwise "1st–3rd". */
function dayWindow(from: number, to: number) {
  return from === to ? ordinal(from) : `${ordinal(from)}–${ordinal(to)}`;
}

/**
 * Refill message for WhatsApp. Only items NOT yet bought this cycle are listed —
 * asking someone to re-buy what they collected yesterday reads as careless and
 * loses trust in the whole message.
 */
function buildRefillMessage(
  row: MonthlyRefillCustomer,
  items: MonthlyRefillItem[],
  storeName: string,
): string {
  const pending = items.filter(i => !i.bought_this_month);
  const alreadyDone = items.length - pending.length;
  const store = storeName ? ` ${storeName}` : '';

  const lines = pending.map((i, n) => {
    const pack = i.pack_size ? ` (${i.pack_size})` : '';
    const qty = Number(i.avg_qty_per_month);
    return `${n + 1}. ${i.medicine_name}${pack} — ${qty % 1 === 0 ? qty : qty.toFixed(1)}`;
  });

  // Tamil first, then English. Customers here are largely Tamil-speaking, and the
  // ask at the end ("add or remove any medicine") is the whole point of the message,
  // so that gets translated too — not just the greeting. Medicine names stay in
  // English because that is how they are printed on the pack.
  return [
    `அன்புள்ள ${row.customer_name}, FirstShield Pharmacy${store} சார்பாக வணக்கம்.`,
    'உங்கள் மாதாந்திர மருந்துகளை புதுப்பிக்கும் நேரம் வந்துவிட்டது. நீங்கள் வழக்கமாக வாங்கும் மருந்துகளின் அடிப்படையில் கீழே பட்டியல் தயாரித்துள்ளோம்:',
    '',
    `Dear ${row.customer_name}, greetings from FirstShield Pharmacy${store}.`,
    'It is time for your monthly refill. Based on your usual monthly purchase, we have listed the medicines below:',
    '',
    ...lines,
    // No price quoted on purpose: medicine rates move between cycles, and a figure
    // in writing reads as a commitment the store may not be able to honour.
    ...(alreadyDone > 0
      ? ['', `(இந்த மாதம் நீங்கள் ஏற்கனவே வாங்கிய ${alreadyDone} மருந்து இந்தப் பட்டியலில் சேர்க்கப்படவில்லை. / ${alreadyDone} item${alreadyDone === 1 ? '' : 's'} already collected this month ${alreadyDone === 1 ? 'is' : 'are'} not included above.)`]
      : []),
    '',
    'இந்தப் பட்டியலைப் பார்வையிட்டு உறுதிப்படுத்தவும். ஏதேனும் மருந்து சேர்க்க அல்லது நீக்க வேண்டும் என்றால் எங்களுக்குத் தெரிவிக்கவும். நாங்கள் உங்களுக்காக தயாராக வைத்திருப்போம்.',
    '',
    'Please review the list and reply to confirm, or let us know if you would like to add or remove any medicine. We will keep them ready for you.',
    '',
    'நன்றி / Thanks,',
    `FirstShield Pharmacy${store}`,
  ].join('\n');
}

function whatsAppLink(mobile: string, message: string): string {
  const digits = (mobile || '').replace(/\D/g, '');
  if (!digits) return '';
  const withCode = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCode}?text=${encodeURIComponent(message)}`;
}

const CONFIDENCE_SX: Record<string, object> = {
  tight: { color: 'text.primary', fontWeight: 600 },
  loose: { color: 'text.secondary', fontWeight: 500 },
  scattered: { color: '#ed6c02', fontWeight: 400, fontStyle: 'italic' },
};

// Bands run cool -> warm with spend, so the high-value regulars stand out when scanning.
const BAND_COLOR: Record<string, string> = {
  '500-1000': '#78909c',
  '1000-1500': '#0288d1',
  '1500-2000': '#f57c00',
  '2000+': '#2e7d32',
};
const BAND_OPTIONS = ['500-1000', '1000-1500', '1500-2000', '2000+'] as const;

const STATUS_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' }> = {
  completed: { label: 'Refill Completed', color: 'success' },
  partial: { label: 'Partial', color: 'warning' },
  pending: { label: 'Refill Pending', color: 'error' },
  active: { label: 'Active (bought recently)', color: 'info' },
};

const STOCK_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
  ok: { label: 'In Stock', color: 'success' },
  short: { label: 'Short', color: 'warning' },
  out: { label: 'Out of Stock', color: 'error' },
};

type SortKey =
  | 'usual_day' | 'customer_name' | 'refill_item_count' | 'median_bill'
  | 'estimated_bill' | 'bought_this_month' | 'coverage_pct' | 'last_purchase_date';

// ─── Expandable customer row, basket fetched on first expand ──────────────────

function CustomerRefillRow({
  row,
  storeId,
  storeName,
  knobs,
  onCompose,
  onCreateOrder,
  selected,
  onToggleSelect,
}: {
  row: MonthlyRefillCustomer;
  storeId: number;
  storeName: string;
  knobs: Record<string, number>;
  onCompose: (row: MonthlyRefillCustomer, items: MonthlyRefillItem[], storeName: string) => void;
  onCreateOrder: (row: MonthlyRefillCustomer) => void;
  selected: boolean;
  onToggleSelect: (customerId: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<MonthlyRefillItem[]>([]);
  const [meta, setMeta] = useState<MonthlyRefillItemsMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [composing, setComposing] = useState(false);

  const loadItems = async (): Promise<MonthlyRefillItem[]> => {
    if (items.length > 0) return items;
    setLoading(true);
    setError('');
    try {
      const res = await monthlyRefillsAPI.getItems(row.customer_id, { store_id: storeId, ...knobs });
      setItems(res.data);
      setMeta(res.metadata);
      return res.data as MonthlyRefillItem[];
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to load refill items');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = () => {
    if (!open) loadItems();
    setOpen(o => !o);
  };

  // The basket is lazy-loaded, so a message can be composed from a collapsed row
  // only after fetching it.
  const handleWhatsApp = async () => {
    setComposing(true);
    try {
      const list = await loadItems();
      if (list.length > 0) onCompose(row, list, storeName);
    } finally {
      setComposing(false);
    }
  };

  const status = STATUS_CHIP[row.refill_status];
  const isOverdue = row.refill_due === 'overdue';

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          '& td': { fontWeight: open ? 600 : 400 },
          ...(isOverdue ? { borderLeft: '3px solid #d32f2f' } : {}),
        }}
        onClick={handleExpand}
        selected={selected}
      >
        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
          <Checkbox size="small" checked={selected} onChange={() => onToggleSelect(row.customer_id)} />
        </TableCell>
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </TableCell>
        <TableCell>
          {row.customer_name}
          {isOverdue && (
            <Tooltip title="Past their usual buying window and not yet refilled">
              <Chip label="Overdue" size="small" color="error" variant="outlined" sx={{ ml: 1, height: 18 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell onClick={e => e.stopPropagation()}>
          {row.customer_number ? (
            <Link href={`tel:${row.customer_number}`} underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Phone sx={{ fontSize: 14 }} />{row.customer_number}
            </Link>
          ) : '—'}
        </TableCell>
        <TableCell>
          {/* Lead with the typical day — that is what the list is sorted on, so the
              column reads in calendar order. The full span goes underneath. */}
          <Tooltip title={`Usually buys around the ${ordinal(row.usual_day)}. Spread ${row.day_spread} days across ${row.active_months} months (${row.qualifying_bills} bills) — ${row.timing_confidence}.`}>
            <Box>
              <Typography variant="body2" sx={CONFIDENCE_SX[row.timing_confidence]}>
                {ordinal(row.usual_day)}
              </Typography>
              {row.usual_day_from !== row.usual_day_to && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                  {dayWindow(row.usual_day_from, row.usual_day_to)}
                </Typography>
              )}
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Tooltip title={`Median of their ${row.qualifying_bills} qualifying bills`}>
            <Box>
              <Typography variant="body2">{inr(row.median_bill)}</Typography>
              <Typography variant="caption" sx={{ display: 'block', color: BAND_COLOR[row.bill_band] }}>
                {row.bill_band}
              </Typography>
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell align="center">{row.refill_item_count}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(row.estimated_bill)}</TableCell>
        <TableCell align="right">
          <Tooltip title={`Counted from ${fmtDate(row.cycle_start)} — the start of this customer's current refill cycle`}>
            <span>{inr(row.bought_this_month)}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          {row.items_bought_this_month}/{row.refill_item_count}
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            {num(row.coverage_pct)}%
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Chip label={status.label} size="small" color={status.color} />
        </TableCell>
        <TableCell align="center">{fmtDate(row.last_purchase_date)}</TableCell>
        <TableCell align="center" onClick={e => e.stopPropagation()}>
          {row.customer_number ? (
            <Tooltip title={row.message_sent_at
              ? `Refill message already sent ${fmtDate(row.message_sent_at)}${row.message_sent_by ? ` by ${row.message_sent_by}` : ''} — click to send again`
              : 'Compose refill message for WhatsApp'}>
              <span>
                <IconButton size="small" onClick={handleWhatsApp} disabled={composing}
                  sx={{ color: row.message_sent_at ? '#9e9e9e' : '#25D366' }}>
                  {composing ? <CircularProgress size={18} /> : <WhatsApp />}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="textSecondary">N/A</Typography>
          )}
          {row.message_sent_at && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
              sent {fmtDate(row.message_sent_at)}
            </Typography>
          )}
        </TableCell>
        <TableCell align="center" onClick={e => e.stopPropagation()}>
          <Tooltip title="Review and create an order for this customer">
            <span>
              <IconButton size="small" color="primary" onClick={() => onCreateOrder(row)}>
                <ShoppingCart />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={14} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#fafafa', borderLeft: '4px solid #1976d2' }}>
              {loading && <CircularProgress size={24} />}
              {!loading && error && <Alert severity="error">{error}</Alert>}
              {!loading && !error && items.length === 0 && (
                <Alert severity="info">No repeat items found for this customer.</Alert>
              )}
              {!loading && !error && items.length > 0 && (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                        <TableCell>Medicine</TableCell>
                        <TableCell>Pack</TableCell>
                        <TableCell>Manufacturer</TableCell>
                        <TableCell align="center">Months</TableCell>
                        <TableCell align="center">Usual Days</TableCell>
                        <TableCell align="right">Avg Qty/Month (units)</TableCell>
                        <TableCell align="right">MRP (₹/unit)</TableCell>
                        <TableCell align="right">Disc %</TableCell>
                        <TableCell align="right">Est. Amount</TableCell>
                        <TableCell align="right">On Hand</TableCell>
                        <TableCell align="center">Stock</TableCell>
                        <TableCell align="center">This Month</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((it, i) => {
                        const stock = STOCK_CHIP[it.stock_status];
                        return (
                          <TableRow key={`${it.medicine_name}|${it.pack_size ?? ''}|${i}`} hover>
                            <TableCell>{it.medicine_name}</TableCell>
                            <TableCell>{it.pack_size || '—'}</TableCell>
                            <TableCell>{it.manufacturer_name || '—'}</TableCell>
                            <TableCell align="center">{it.months_seen}</TableCell>
                            <TableCell align="center">{dayWindow(it.item_day_from, it.item_day_to)}</TableCell>
                            <TableCell align="right">{num(it.avg_qty_per_month, 1)}</TableCell>
                            <TableCell align="right">{it.unit_mrp == null ? '—' : `₹${num(it.unit_mrp, 2)}`}</TableCell>
                            <TableCell align="right">{num(it.discount_pct, 1)}%</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(it.est_amount)}</TableCell>
                            <TableCell align="right"
                              sx={{ color: it.stock_status === 'ok' ? 'text.primary' : '#c62828', fontWeight: it.stock_status === 'ok' ? 400 : 700 }}>
                              {num(it.current_stock)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={stock.label} size="small" color={stock.color}
                                variant={it.stock_status === 'ok' ? 'outlined' : 'filled'} />
                            </TableCell>
                            <TableCell align="center">
                              {it.bought_this_month
                                ? <Chip label={`✓ ${num(it.qty_bought_this_month)}`} size="small" color="success" variant="outlined" />
                                : <Typography variant="caption" color="textSecondary">—</Typography>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {meta && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                      {meta.item_count} items · Est. {inr(meta.estimated_bill)} ·{' '}
                      {meta.items_refilled} already refilled · {meta.items_short} short or out of stock
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────

const MonthlyRefills: React.FC<Props> = ({ selectedStore }) => {
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    lookupAPI.getStores().then(setStores).catch(() => {});
  }, []);

  const [storeId, setStoreId] = useState<number | ''>(selectedStore ? selectedStore.id : '');
  const [minBill, setMinBill] = useState('500');
  const [lookbackMonths, setLookbackMonths] = useState('3');
  const [minMonthsQual, setMinMonthsQual] = useState('2');
  const [minMonthsItem, setMinMonthsItem] = useState('2');
  const [completedPct, setCompletedPct] = useState('80');
  const [churnDays, setChurnDays] = useState('28');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'partial' | 'completed' | 'active'>('all');
  const [bandFilter, setBandFilter] = useState<'all' | typeof BAND_OPTIONS[number]>('all');

  const [rows, setRows] = useState<MonthlyRefillCustomer[]>([]);
  const [meta, setMeta] = useState<MonthlyRefillsMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [sortKey, setSortKey] = useState<SortKey>('usual_day');
  const [sortAsc, setSortAsc] = useState(true);

  // Staff review the exact text before it goes out — the deep link prefills
  // WhatsApp, so whatever is in this box is what the customer receives.
  const [compose, setCompose] = useState<{
    row: MonthlyRefillCustomer; items: MonthlyRefillItem[]; text: string;
  } | null>(null);
  const [sendError, setSendError] = useState('');

  // Selection lives here, not in the row: rows own only their own basket.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [orderFor, setOrderFor] = useState<{ ids: number[]; name?: string } | null>(null);
  const [success, setSuccess] = useState('');

  const toggleSelect = (customerId: number) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId); else next.add(customerId);
      return next;
    });

  const openCompose = (row: MonthlyRefillCustomer, items: MonthlyRefillItem[], storeName: string) => {
    setSendError('');
    setCompose({ row, items, text: buildRefillMessage(row, items, storeName) });
  };

  const sendWhatsApp = async () => {
    if (!compose) return;
    const link = whatsAppLink(compose.row.customer_number, compose.text);
    if (!link) { setSendError('This customer has no usable mobile number.'); return; }

    // Open first: if the popup is blocked the staff member sees it immediately,
    // and nothing is logged as sent when it wasn't.
    const win = window.open(link, '_blank');
    if (!win) { setSendError('Your browser blocked the WhatsApp window. Allow pop-ups for this site and try again.'); return; }

    const pending = compose.items.filter(i => !i.bought_this_month);
    try {
      await monthlyRefillsAPI.markMessaged(compose.row.customer_id, {
        store_id: storeId as number,
        cycle_start: dayjs(compose.row.cycle_start).format('YYYY-MM-DD'),
        item_count: pending.length,
        estimated_amount: pending.reduce((s, i) => s + Number(i.est_amount || 0), 0),
      });
      setRows(prev => prev.map(r => r.customer_id === compose.row.customer_id
        ? { ...r, message_sent_at: new Date().toISOString() } : r));
    } catch {
      // WhatsApp is already open; failing to log it must not look like a failed send.
      console.error('Failed to record refill message as sent');
    }
    setCompose(null);
  };

  // Knobs the drill-down must repeat so its repeat-item set matches the list's.
  const itemKnobs = {
    lookback_months: Number(lookbackMonths),
    min_bill_amount: Number(minBill),
    min_months_qualifying: Number(minMonthsQual),
    min_months_item: Number(minMonthsItem),
  };

  // Deliberately not auto-firing on knob edits — only on store change and Recalculate.
  const loadReport = async () => {
    if (!storeId) return;
    setLoading(true);
    setError('');
    try {
      const res = await monthlyRefillsAPI.getList({
        store_id: storeId,
        ...itemKnobs,
        completed_threshold_pct: Number(completedPct),
        churn_days: Number(churnDays),
      });
      setRows(res.data);
      setMeta(res.metadata);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to load monthly refills');
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storeId) loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const visibleRows = rows.filter(r =>
    (statusFilter === 'all' || r.refill_status === statusFilter) &&
    (bandFilter === 'all' || r.bill_band === bandFilter));

  const sortedRows = [...visibleRows].sort((a, b) => {
    let av: any = a[sortKey];
    let bv: any = b[sortKey];
    if (sortKey === 'customer_name') {
      av = String(av || '').toLowerCase();
      bv = String(bv || '').toLowerCase();
    } else if (sortKey === 'last_purchase_date') {
      av = av ? dayjs(av).valueOf() : 0;
      bv = bv ? dayjs(bv).valueOf() : 0;
    } else {
      av = Number(av) || 0;
      bv = Number(bv) || 0;
    }
    if (av < bv) return sortAsc ? -1 : 1;
    if (av > bv) return sortAsc ? 1 : -1;
    // Ties inside a shared buying day fall back to value.
    return Number(b.estimated_bill) - Number(a.estimated_bill);
  });

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === 'usual_day' || key === 'customer_name');
    }
  };

  // Customer names are free text and can contain commas — quote every field.
  const csvCell = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;

  const exportCSV = () => {
    const headers = [
      'Customer', 'Mobile', 'Usual Day', 'Usual Days Range', 'Timing', 'Typical Bill', 'Bill Band', 'Refill Items', 'Est. Bill',
      'Bought This Cycle', 'Items Bought', 'Coverage %', 'Status', 'Due', 'Last Purchase',
    ];
    const csvRows = sortedRows.map(r => [
      r.customer_name,
      // leading tab keeps Excel from turning the number into scientific notation
      `\t${r.customer_number}`,
      ordinal(r.usual_day),
      dayWindow(r.usual_day_from, r.usual_day_to),
      r.timing_confidence,
      r.median_bill,
      r.bill_band,
      r.refill_item_count,
      r.estimated_bill,
      r.bought_this_month,
      `${r.items_bought_this_month}/${r.refill_item_count}`,
      r.coverage_pct ?? 0,
      STATUS_CHIP[r.refill_status].label,
      r.refill_due,
      r.last_purchase_date || '',
    ]);
    const csv = [headers, ...csvRows].map(row => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monthly_refills_store${storeId}_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const knobField = (label: string, value: string, set: (v: string) => void, help: string, width = 120) => (
    <Tooltip title={help}>
      <TextField label={label} value={value} onChange={e => set(e.target.value)}
        size="small" type="number" sx={{ width }} />
    </Tooltip>
  );

  const tile = (label: string, value: string, color?: string, sub?: string) => (
    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
      <Typography variant="caption" color="textSecondary">{label}</Typography>
      {sub && <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{sub}</Typography>}
    </Paper>
  );

  const sortHead = (key: SortKey, label: string, align: 'left' | 'right' | 'center' = 'left') => (
    <TableCell align={align} sortDirection={sortKey === key ? (sortAsc ? 'asc' : 'desc') : false}>
      <TableSortLabel active={sortKey === key} direction={sortAsc ? 'asc' : 'desc'} onClick={() => handleSort(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Store</InputLabel>
            <Select value={storeId} label="Store" onChange={e => setStoreId(e.target.value as number)}>
              {stores.map(s => <MenuItem key={s.id} value={s.id}>{s.store_name}</MenuItem>)}
            </Select>
          </FormControl>

          {knobField('Min bill (₹)', minBill, setMinBill, 'A bill must total at least this to count as a refill visit')}
          {knobField('Lookback (months)', lookbackMonths, setLookbackMonths, 'Complete calendar months analysed before the current one', 140)}
          {knobField('Qualifying months', minMonthsQual, setMinMonthsQual, 'How many of those months need a qualifying bill', 145)}
          {knobField('Item repeats in', minMonthsItem, setMinMonthsItem, 'An item must appear in at least this many months to be a refill item', 130)}
          {knobField('Completed at %', completedPct, setCompletedPct, 'Share of refill items bought this month to count as Completed', 130)}
          {knobField('Churned after (days)', churnDays, setChurnDays, 'A refill customer only counts as Pending if they have bought nothing at all for this many days. If they bought anything more recently — even a switched medicine — they show as Active, not Pending.', 155)}

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Bill band</InputLabel>
            <Select value={bandFilter} label="Bill band" onChange={e => setBandFilter(e.target.value as any)}>
              <MenuItem value="all">All bands</MenuItem>
              {BAND_OPTIONS.map(b => (
                <MenuItem key={b} value={b}>
                  {b === '2000+' ? 'Above ₹2,000' : `₹${b.split('-')[0]} – ₹${b.split('-')[1]}`}
                  {meta ? ` (${meta.bands?.[b] ?? 0})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value as any)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="active">Active (bought recently)</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>

          <Button variant="contained" onClick={loadReport} disabled={!storeId || loading} startIcon={<EventRepeat />}>
            Recalculate
          </Button>
          <Button variant="contained" color="primary" startIcon={<ShoppingCart />}
            disabled={selectedIds.size === 0}
            onClick={() => setOrderFor({ ids: Array.from(selectedIds) })}>
            Create Order ({selectedIds.size} selected)
          </Button>
          <Button size="small" startIcon={<Download />} onClick={exportCSV}
            disabled={sortedRows.length === 0} sx={{ ml: 'auto' }}>
            CSV
          </Button>
        </Box>
      </Paper>

      {meta && !loading && !error && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>{tile('Monthly Customers', String(meta.total_customers))}</Grid>
          <Grid item xs={6} md={3}>
            {tile('Refill Pending', String(meta.pending), '#c62828', `${meta.overdue} past their usual window`)}
          </Grid>
          <Grid item xs={6} md={3}>
            {tile('Partial / Completed', `${meta.partial} / ${meta.completed}`, '#2e7d32')}
          </Grid>
          <Grid item xs={6} md={3}>
            {tile('Est. Refill Value', inr(meta.estimated_revenue), '#1976d2', `${inr(meta.captured_revenue)} billed so far`)}
          </Grid>
        </Grid>
      )}

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Customers with a bill of {inr(Number(minBill))} or more in at least {minMonthsQual} of the last{' '}
        {lookbackMonths} complete months, and the items they buy again month after month. Sorted by the
        part of the month they usually come in, so the list reads top-to-bottom as a call schedule.
        Expand a row for the basket, its value, and whether the stock is on the shelf. A{' '}
        <em>scattered</em> window means their timing is irregular — treat that date range with caution.
        Refills are counted over each customer's own cycle rather than the calendar month, so someone
        who always buys on the 31st is credited for that visit instead of showing up as pending on the 1st.
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && !error && sortedRows.length === 0 && (
        <Alert severity="info">
          No monthly refill customers found for this store with the current settings. Try lowering the
          minimum bill amount or the number of qualifying months.
        </Alert>
      )}

      {!loading && !error && sortedRows.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 600 } }}>
                <TableCell padding="checkbox">
                  <Checkbox size="small"
                    checked={sortedRows.length > 0 && selectedIds.size === sortedRows.length}
                    indeterminate={selectedIds.size > 0 && selectedIds.size < sortedRows.length}
                    onChange={e => setSelectedIds(e.target.checked
                      ? new Set(sortedRows.map(r => r.customer_id)) : new Set())}
                  />
                </TableCell>
                <TableCell sx={{ width: 48 }} />
                {sortHead('customer_name', 'Customer')}
                <TableCell>Mobile</TableCell>
                {sortHead('usual_day', 'Usual Days')}
                {sortHead('median_bill', 'Typical Bill', 'right')}
                {sortHead('refill_item_count', 'Refill Items', 'center')}
                {sortHead('estimated_bill', 'Est. Bill', 'right')}
                {sortHead('bought_this_month', 'Bought This Cycle', 'right')}
                {sortHead('coverage_pct', 'Coverage', 'center')}
                <TableCell align="center">Status</TableCell>
                {sortHead('last_purchase_date', 'Last Purchase', 'center')}
                <TableCell align="center">WhatsApp</TableCell>
                <TableCell align="center">Order</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map(r => (
                <CustomerRefillRow
                  key={r.customer_id}
                  row={r}
                  storeId={storeId as number}
                  storeName={stores.find(s => s.id === storeId)?.store_name || ''}
                  knobs={itemKnobs}
                  onCompose={openCompose}
                  onCreateOrder={r => setOrderFor({ ids: [r.customer_id], name: r.customer_name })}
                  selected={selectedIds.has(r.customer_id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {meta?.capped && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Showing the first 2000 customers only — narrow the filters to see the rest.
        </Alert>
      )}

      {orderFor && storeId && (
        <MonthlyRefillOrderReview
          open={!!orderFor}
          onClose={() => setOrderFor(null)}
          storeId={storeId as number}
          storeName={stores.find(s => s.id === storeId)?.store_name || ''}
          customerIds={orderFor.ids}
          customerName={orderFor.name}
          knobs={itemKnobs}
          onCreated={msg => { setSuccess(msg); setSelectedIds(new Set()); }}
        />
      )}

      <Dialog open={!!compose} onClose={() => setCompose(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsApp sx={{ color: '#25D366' }} />
          Refill message — {compose?.row.customer_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Sends to <strong>{compose?.row.customer_number}</strong> through your own WhatsApp.
            Edit the text below if needed — this is exactly what the customer will see.
            {compose && compose.items.some(i => i.bought_this_month) && (
              <> Items already collected this month are left out on purpose.</>
            )}
          </Typography>

          {compose && compose.items.some(i => !i.bought_this_month && i.stock_status !== 'ok') && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {compose.items.filter(i => !i.bought_this_month && i.stock_status !== 'ok').length} of these
              are short or out of stock right now — check before promising a date.
            </Alert>
          )}

          {compose?.row.message_sent_at && (
            <Alert severity="info" sx={{ mb: 2 }}>
              A refill message was already sent to this customer on {fmtDate(compose.row.message_sent_at)}.
            </Alert>
          )}

          <TextField
            multiline minRows={12} fullWidth value={compose?.text || ''}
            onChange={e => setCompose(c => (c ? { ...c, text: e.target.value } : c))}
            InputProps={{ sx: { fontSize: 14, fontFamily: 'inherit' } }}
          />

          {sendError && <Alert severity="error" sx={{ mt: 2 }}>{sendError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompose(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<WhatsApp />} onClick={sendWhatsApp}
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1da851' } }}>
            Open in WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthlyRefills;
